import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Card from './Card';
import Button from './Button';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import EventModal from '../modals/EventModal';
import { APP_CONFIG } from '../../config/constants';
import { useToast } from './Toast';

const { FiChevronLeft, FiChevronRight, FiPlus } = FiIcons;

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const EVENT_COLORS = {
  sage: 'bg-sage',
  goldenrod: 'bg-goldenrod',
  skyblue: 'bg-skyblue',
  lavender: 'bg-lavender',
  crimson: 'bg-crimson',
};

export default function Calendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const { success } = useToast();

  // Load events from localStorage
  useEffect(() => {
    try {
      const savedEvents = localStorage.getItem(APP_CONFIG.storage.calendar);
      if (savedEvents) {
        setEvents(JSON.parse(savedEvents));
      }
    } catch (e) {
      console.error("Failed to load calendar events:", e);
      setEvents([]);
    }
  }, []);

  // Save events to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(APP_CONFIG.storage.calendar, JSON.stringify(events));
    } catch (e) {
      console.error("Failed to save calendar events:", e);
    }
  }, [events]);

  const calendarGrid = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];

    const prevMonthDays = new Date(year, month, 0).getDate();
    for (let i = firstDayOfMonth - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonthDays - i);
      days.push({ key: `prev-${i}`, day: prevMonthDays - i, date: date.toISOString().split('T')[0] });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      const dateString = date.toISOString().split('T')[0];
      days.push({
        key: dateString,
        day: i,
        date: dateString,
        isCurrentMonth: true,
        isToday: dateString === new Date().toISOString().split('T')[0],
      });
    }

    const totalCells = 42;
    const nextMonthDaysCount = totalCells - days.length;
    for (let i = 1; i <= nextMonthDaysCount; i++) {
      const date = new Date(year, month + 1, i);
      days.push({ key: `next-${i}`, day: i, date: date.toISOString().split('T')[0] });
    }

    return days;
  }, [currentDate]);

  const handlePrevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const handleNextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  const handleToday = () => setCurrentDate(new Date());

  const openAddModal = (date) => {
    setSelectedDate(date);
    setEditingEvent(null);
    setIsModalOpen(true);
  };

  const openEditModal = (event) => {
    setEditingEvent(event);
    setSelectedDate(event.date);
    setIsModalOpen(true);
  };

  const handleSaveEvent = (eventData) => {
    if (eventData.id) {
      setEvents(events.map(e => e.id === eventData.id ? eventData : e));
      success('Event updated!');
    } else {
      setEvents([...events, { ...eventData, id: Date.now().toString() }]);
      success('Event added!');
    }
  };

  const handleDeleteEvent = (eventId) => {
    setEvents(events.filter(e => e.id !== eventId));
    success('Event deleted!');
  };

  const eventsByDate = useMemo(() => {
    return events.reduce((acc, event) => {
      const date = event.date;
      if (!acc[date]) acc[date] = [];
      acc[date].push(event);
      acc[date].sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
      return acc;
    }, {});
  }, [events]);

  return (
    <div className="flex flex-col h-full w-full">
      <Card className="flex-1 flex flex-col p-4 lg:p-6">
        <header className="flex flex-col lg:flex-row items-center justify-between mb-4 gap-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center">
              <Button variant="secondary" size="sm" onClick={handlePrevMonth} className="!p-2.5">
                <SafeIcon icon={FiChevronLeft} className="w-5 h-5" />
              </Button>
              <h2 className="text-xl lg:text-2xl font-cormorant font-bold text-charcoal mx-4 w-48 text-center">
                {currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
              </h2>
              <Button variant="secondary" size="sm" onClick={handleNextMonth} className="!p-2.5">
                <SafeIcon icon={FiChevronRight} className="w-5 h-5" />
              </Button>
            </div>
            <Button variant="secondary" size="sm" onClick={handleToday}>Today</Button>
          </div>
          <Button variant="primary" icon={FiPlus} onClick={() => openAddModal(new Date().toISOString().split('T')[0])}>
            Add Event
          </Button>
        </header>
        <div className="flex-1 grid grid-cols-7 gap-1 lg:gap-2">
          {WEEKDAYS.map(day => (
            <div key={day} className="text-center font-dm-sans font-semibold text-charcoal/60 text-sm pb-2">
              {day}
            </div>
          ))}
          {calendarGrid.map(day => (
            <div
              key={day.key}
              className={`border border-sage/10 rounded-lg p-2 flex flex-col transition-colors min-h-[120px] ${day.isCurrentMonth ? 'bg-white/50 hover:bg-sage/10 cursor-pointer' : 'bg-beige/50 text-charcoal/40'}`}
              onClick={() => day.isCurrentMonth && openAddModal(day.date)}
            >
              <div className={`font-dm-sans font-semibold mb-1 ${day.isToday ? 'text-white bg-sage rounded-full w-7 h-7 flex items-center justify-center' : ''}`}>
                {day.day}
              </div>
              <div className="flex-1 space-y-1 overflow-y-auto">
                {(eventsByDate[day.date] || []).map(event => (
                  <motion.div
                    key={event.id}
                    layout
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    onClick={(e) => { e.stopPropagation(); openEditModal(event); }}
                    className={`w-full p-1.5 rounded-md ${EVENT_COLORS[event.color] || 'bg-sage'} text-white text-xs truncate`}
                  >
                    {event.startTime && <span>{event.startTime} </span>}
                    {event.title}
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>
      <EventModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveEvent}
        onDelete={handleDeleteEvent}
        event={editingEvent}
        selectedDate={selectedDate}
      />
    </div>
  );
}